'use client';

import {
  createAvatar,
  SquishEngine,
  type SquishLayout,
} from '@usespaceui/squishmoji';
import { MotionConfigContext, useInView } from 'motion/react';
import {
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useVoiceAvatarGaze } from '@/components/voice/use-voice-avatar-gaze';
import type { YourImaiPersona } from './your-imai-persona';

type Look = Pick<
  YourImaiPersona,
  'shape' | 'expression' | 'backgroundStyle' | 'colorSeed'
>;

/** Keep the generator's body, colour treatment and sizing; own only its eyes. */
export function YourImaiTrackingAvatar({
  persona,
  size,
  className,
}: {
  persona: Look;
  size: number;
  className?: string;
}) {
  const hostRef = useRef<HTMLSpanElement>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const gazeRef = useRef<SVGGElement>(null);
  const inView = useInView(hostRef);
  const config = useContext(MotionConfigContext);
  const [visible, setVisible] = useState(true);
  const { colorSeed, shape, expression, backgroundStyle } = persona;
  const generated = useMemo(
    () =>
      createAvatar(colorSeed, {
        shape,
        expression,
        backgroundStyle,
        size,
        format: 'json',
      }),
    [colorSeed, shape, expression, backgroundStyle, size]
  );
  const layoutRef = useRef(generated.layout);
  const engineRef = useRef<SquishEngine | null>(null);
  const timeRef = useRef(0);
  const running = inView && visible && config.reducedMotion !== 'always';
  const { hasPointer: following, refresh } = useVoiceAvatarGaze({
    svgRef,
    gazeRef,
    enabled: running,
    layoutRef,
    identity: generated.svg,
  });

  useEffect(() => {
    const update = () => setVisible(document.visibilityState === 'visible');
    update();
    document.addEventListener('visibilitychange', update);
    return () => document.removeEventListener('visibilitychange', update);
  }, []);

  useLayoutEffect(() => {
    const svg = hostRef.current?.querySelector('svg');
    if (!svg) return;
    const groups = [...svg.querySelectorAll<SVGGElement>('[data-agent-gaze]')];
    const group =
      groups.find((candidate) => candidate.children.length > 0) ??
      document.createElementNS('http://www.w3.org/2000/svg', 'g');
    groups
      .filter((candidate) => candidate !== group)
      .forEach((candidate) => candidate.remove());
    group.setAttribute('data-agent-gaze', '');
    group.setAttribute('transform', 'translate(0 0)');
    for (const eye of svg.querySelectorAll('[data-sq-eye]')) {
      const rect = document.createElementNS(
        'http://www.w3.org/2000/svg',
        'rect'
      );
      rect.setAttribute(
        'fill',
        eye.getAttribute('fill') ?? generated.layout.colors.eye
      );
      group.append(rect);
      eye.remove();
    }
    svg.append(group);
    svgRef.current = svg;
    gazeRef.current = group;
    layoutRef.current = generated.layout;
    return () => {
      svgRef.current = null;
      gazeRef.current = null;
    };
  }, [generated]);

  useEffect(() => {
    const bodies = [...(svgRef.current?.querySelectorAll('path') ?? [])];
    const eyes = gazeRef.current;
    if (!bodies.length || !eyes) return;
    const engine =
      engineRef.current ?? new SquishEngine(colorSeed, { shape, expression });
    engineRef.current = engine;
    engine.name = colorSeed;
    engine.setShape(shape, timeRef.current);
    engine.setExpression(expression, timeRef.current);
    let frame = 0;
    const paint = (layout: SquishLayout) => {
      layoutRef.current = layout;
      bodies.forEach((body) => body.setAttribute('d', layout.body));
      layout.eyes.forEach((eye, index) => {
        const rect = eyes.children[index];
        if (!rect) return;
        const matrix = [...eye.matrix];
        rect.setAttribute('x', String(-eye.width / 2));
        rect.setAttribute('y', String(-eye.height / 2));
        rect.setAttribute('width', String(eye.width));
        rect.setAttribute('height', String(eye.height));
        rect.setAttribute('rx', String(Math.min(eye.width, eye.height) / 2));
        rect.setAttribute('transform', `matrix(${matrix.join(' ')})`);
      });
      refresh();
    };
    paint(running ? engine.layout(timeRef.current) : generated.layout);
    if (!running) return;
    const draw = (now: number) => {
      const time = now / 1000;
      timeRef.current = time;
      const phase = (time + 1.35) % 4.7;
      const lid =
        phase > 0.18 ? 1 : Math.max(0.035, Math.abs(phase / 0.09 - 1));
      paint(
        engine.layout(time, (fitted) =>
          fitted.map((eye) => {
            const matrix: typeof eye.matrix = [...eye.matrix];
            matrix[3] *= lid;
            if (!following.current) {
              matrix[4] += Math.sin(time * 0.64 + 0.8) * 12.4;
              matrix[5] += Math.sin(time * 0.43 + 2.2) * 9.6;
            }
            return { ...eye, matrix };
          })
        )
      );
      frame = requestAnimationFrame(draw);
    };
    frame = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(frame);
  }, [colorSeed, shape, expression, generated, running, following, refresh]);

  return (
    <span
      ref={hostRef}
      className={className}
      style={{ display: 'inline-block', lineHeight: 0 }}
      dangerouslySetInnerHTML={{ __html: generated.svg }}
    />
  );
}
